import { UI_CONFIG } from '@porterin/booking-flow/src/auth/domain/config/ui';
import { AuthModalConfig } from '@porterin/booking-flow/src/auth/domain/types/AuthModalConfig';
import AuthModal from '@porterin/booking-flow/src/auth/view/components';
import {
  AuthContextType as BookingFlowAuthContextType,
  User as BookingFlowUser,
} from '@porterin/booking-flow/src/order-review/view/contexts/auth/AuthContext';
import { useLocaleContext } from '@porterin/component-library/src/contexts/locale/LocaleContext';
import LocaleProviderWrapper from '@porterin/component-library/src/providers/LocaleProviderWrapper';
import {
  authContextConverter,
  authUserConverter,
} from '@porterin/feature/src/containers/bindings/helpers/BookingFlowBindingsHelper';
import { useAuthContext } from '@porterin/feature/src/containers/login/view-model/contexts/auth-context/AuthContextV1';
import React from 'react';
import ReactModal from 'react-modal';

import { PNM_REQUIREMENT_TYPE } from '../../domain/store/PnMStore';
import styles from '../styles/PnmModal.module.scss';

import { make as PnmBookingFlowLazyCmp } from './PnmBookingFlowLazyCmp.mjs';

interface PnmBookingFlowModalCmpProps {
  modal_state: boolean;
  close_modal: () => void;
  sr_external_id: string | undefined;
  mobile: string;
}

interface PnmAuthModalCmpProps {
  modal_state: boolean;
  close_modal: () => void;
  mobile: string;
}

function PnmAuthModalCmp({
  modal_state,
  close_modal,
  mobile,
}: Readonly<PnmAuthModalCmpProps>): React.ReactElement {
  const { country } = useLocaleContext();
  const authContext = useAuthContext();
  const { set_user, set_is_authenticated } = authContext;

  const config: AuthModalConfig = {
    mobileRegex: country.mobileRegex,
    mobileCode: country.mobileCode,
  };

  const handleOtpValid = (authenticated_user?: BookingFlowUser): void => {
    if (authenticated_user !== undefined) {
      const auth_user = authUserConverter(authenticated_user);
      set_user({ ...auth_user, mobile: auth_user.mobile ?? mobile });
    }
    set_is_authenticated(true);
  };

  return (
    <AuthModal
      isOpen={modal_state}
      variant="login"
      config={config}
      onClose={close_modal}
      prefillData={{
        mobile,
        name: undefined,
        requirement: PNM_REQUIREMENT_TYPE,
      }}
      authContext={
        authContextConverter(authContext) as BookingFlowAuthContextType
      }
      onOtpValid={handleOtpValid}
      lockMobile
      showChangeMobile={false}
      closeOnSuccess={false}
      modalCloseTimeout={UI_CONFIG.MODAL_CLOSE_TIMEOUT}
    />
  );
}

function PnmBookingFlowModalCmp({
  modal_state,
  close_modal,
  sr_external_id,
  mobile,
}: Readonly<PnmBookingFlowModalCmpProps>): React.ReactElement | null {
  const { is_authenticated, user } = useAuthContext();

  if (!is_authenticated) {
    return (
      <LocaleProviderWrapper>
        <PnmAuthModalCmp
          modal_state={modal_state}
          close_modal={close_modal}
          mobile={mobile}
        />
      </LocaleProviderWrapper>
    );
  }

  if (user !== undefined) {
    return (
      <ReactModal
        isOpen={modal_state}
        onRequestClose={close_modal}
        closeTimeoutMS={UI_CONFIG.MODAL_CLOSE_TIMEOUT}
        overlayClassName={styles['overlay']}
        className={styles['modal-container']}
        ariaHideApp={false}
        shouldCloseOnOverlayClick
      >
        <PnmBookingFlowLazyCmp
          sr_external_id={sr_external_id}
          user={user}
          hide_pnm_booking_flow={close_modal}
        />
      </ReactModal>
    );
  }

  return null;
}

export { PnmAuthModalCmp };
export default PnmBookingFlowModalCmp;
